import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import VueCompositionApi from '@vue/composition-api'
import upperFirst from 'lodash/upperFirst'
import camelCase from 'lodash/camelCase'
import CoreuiVue from '@coreui/vue'
import VCalendar from 'v-calendar'
import vueSmoothScroll from 'vue2-smooth-scroll'
import VueClipboard from 'vue-clipboard2'
import * as Sentry from '@sentry/vue'
import pako from 'pako'
require('@/assets/sass/index.scss')

if (process.env.NODE_ENV === 'local' && process.env.VUE_APP_MSW_MODE) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { worker } = require('../msw/browser')
  worker.start()
}

import {
  cilSpeedometer,
  cilUser,
  cilChild,
  cilCalendar,
  cilHome,
  cilCarAlt,
  cilSchool,
  cilHandPointUp,
  cilList,
  cilCopy,
  cilCloud,
  cilOptions,
  cilNotes,
  cilBuilding,
  cilPrint,
  cilCommentBubble,
  cilBullhorn,
  cilBusAlt,
} from '@coreui/icons'
// import { iconsSet } from '@/lib/icons/icons'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
Vue.use(CoreuiVue)
Vue.use(VCalendar)
Vue.use(VueCompositionApi)
Vue.use(vueSmoothScroll)
Vue.use(VueClipboard)

library.add(fas)
Vue.component('font-awesome-icon', FontAwesomeIcon)

Vue.config.productionTip = false

const requireComponent = require.context('./components/plugins/', true, /.vue$/)
requireComponent.keys().forEach((fileName: string) => {
  const componentConfig = requireComponent(fileName)
  const componentName = upperFirst(camelCase(fileName.split('/').pop()))

  Vue.component(
    componentName.replace('Vue', ''),
    componentConfig.default || componentConfig
  )
})

const requireBasicSurveyComponent = require.context(
  '@/components/organisms/basicSurvey/',
  true,
  /.vue$/
)

// ./components/organisms/basicSurvey/以下をglobal登録する
requireBasicSurveyComponent.keys().forEach(fileName => {
  // コンポーネント設定を取得する
  const componentConfig = requireBasicSurveyComponent(fileName)

  // コンポーネント名をパスカルケース (PascalCase) で取得する
  const componentName = upperFirst(camelCase(fileName.split('/').pop()))

  // コンポーネントをグローバル登録する
  Vue.component(
    componentName.replace('Vue', ''),
    // `export default` を使ってコンポーネントがエクスポートされた場合に存在する
    // `.default` でコンポーネントオプションを期待していて
    // 存在しない場合にはモジュールのルートにフォールバックします。
    componentConfig.default || componentConfig
  )
})

declare global {
  interface Window {
    google: {
      maps: Record<string, any>
    }
  }
}

// セッションストレージからスタッフ情報を取得
const getSessionCurrentStaffData = sessionStorage
  .getItem('currentStaff')
  ?.split(',') as Iterable<number>
let decompressedStaffData: any
if (getSessionCurrentStaffData) {
  decompressedStaffData = JSON.parse(
    pako.inflate(new Uint8Array(getSessionCurrentStaffData), { to: 'string' })
  )
}

// セッションストレージから児童情報を取得
const getSessionChildData = sessionStorage.getItem('cldId')
let decompressedChildId: string | undefined
if (getSessionChildData) {
  decompressedChildId = JSON.parse(
    pako.inflate(new Uint8Array(getSessionChildData.split(',').map(Number)), {
      to: 'string',
    })
  )
}

Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN || '',
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.httpClientIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: process.env.NODE_ENV,
  initialScope: {
    user: {
      id: decompressedStaffData ? decompressedStaffData.staff_id : 0,
      officeId: decompressedStaffData ? decompressedStaffData.office_id : 0,
      childId: decompressedChildId ?? '',
    },
  },
  beforeSend(event, hint) {
    const error: any = hint.originalException
    if (
      error &&
      error.response &&
      error.response.status === 400 &&
      error.config &&
      error.config.url &&
      error.config.url.indexOf('/oauth2/token') !== -1
    ) {
      // oauth2/token の400エラーは送信しない
      return null
    }
    return event
  },
})

new Vue({
  router,
  store,
  icons: {
    cilSpeedometer,
    cilUser,
    cilChild,
    cilCalendar,
    cilHome,
    cilCarAlt,
    cilSchool,
    cilHandPointUp,
    cilList,
    cilCopy,
    cilCloud,
    cilOptions,
    cilNotes,
    cilBuilding,
    cilPrint,
    cilCommentBubble,
    cilBullhorn,
    cilBusAlt,
  },
  render: h => h(App),
}).$mount('#app')
